import { type JSX, useMemo, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { Button } from 'Components/Buttons/Buttons';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { ICON_POSITIVE } from 'Config/Icons';
import { DueDiligenceControl } from 'Models/DueDiligence';
import { UploadedFile } from 'Models/Files';
import { Effectiveness } from 'Models/OperationalControls';
import { DueDiligenceModule } from 'Models/ProductModule';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';

import styles from './ControlView.module.css';
import { ControlAssessment } from '../ControlAssessment/ControlAssessment';
import { QuestionView } from '../QuestionView/QuestionView';

export interface ControlViewProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    assessmentId: string;
    assessmentSubjectId: string;
    control: DueDiligenceControl;
    documentApi: DocumentApi;
    existingPendingFiles: File[];
    issueIdToTitleMap: Map<string, string>;
    lastSavedAssessment: TprmOrUcrmDueDiligenceAssessment;
    module: DueDiligenceModule;
    onAiError: (message: string) => void;
    onAssessmentUpdated: (updatedAssessment: TprmOrUcrmDueDiligenceAssessment) => void;
    onChange: (effectiveness: Effectiveness | undefined, comments: string, mappedFiles?: UploadedFile[]) => void;
    onSaveFileChanges: (newPendingFiles: File[], selectedPendingFiles: File[], selectedUploadedFiles: UploadedFile[]) => void;
    organizationId: string;
    selectedPendingFiles: File[];

    disabled?: boolean;
}

/**
 * Renders the UI for a control within a due diligence assessment.
 * Includes all questions for the control (showing the questionnaire responses, from the third party in TPRM or the respondents in UCRM, if present) and the ability to assess the control.
 */
export const ControlView = (props: ControlViewProps): JSX.Element => {
    const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);

    const questionnaireAnswersChangedSinceLastSave = useMemo(() => {
        const controlLastUpdatedTimestamp = props.control.last_updated_at;
        if (!controlLastUpdatedTimestamp) {
            return false;
        }
        return props.control.questions.some((question) => question.last_updated_at && question.last_updated_at > controlLastUpdatedTimestamp);
    }, [props.control.last_updated_at, props.control.questions]);

    const handleAcknowledgeUpdates = async (): Promise<string> => {
        const [framework, group, control] = props.control.identifier.split('#');
        const response = await props.assessmentApi.acknowledgeControlUpdates(props.organizationId, props.assessmentSubjectId, props.assessmentId, framework, group, control);
        props.onAssessmentUpdated(response.data);
        return 'Control assessment marked as valid.';
    };

    return (
        <div className={styles.pageBodySection}>
            <div className={styles.controlHeader}>
                <Text variant="Header2" noStyles>
                    {props.control.name}
                </Text>
                <Text color="darkGray" noStyles>
                    {props.control.text}
                </Text>
                {questionnaireAnswersChangedSinceLastSave && (
                    <div className={styles.updatedQuestionsNotice}>
                        <Text variant="Text3" color="darkGray" noStyles>
                            Questions were updated since the control was last assessed.
                        </Text>
                        <Button variant="linkText" onClick={() => setShowAcknowledgeModal(true)} size="sm">
                            Mark assessment as valid
                        </Button>
                    </div>
                )}
            </div>
            <hr />
            <div className={styles.questionsContainer}>
                {props.control.questions.map((question) => (
                    <QuestionView key={question.id} question={question} documentApi={props.documentApi} issueIdToTitleMap={props.issueIdToTitleMap} module={props.module} controlLastSavedAt={props.control.last_updated_at} />
                ))}
            </div>
            <ControlAssessment artificialIntelligenceApi={props.artificialIntelligenceApi} control={props.control} module={props.module} disabled={props.disabled} documentApi={props.documentApi} existingPendingFiles={props.existingPendingFiles} lastSavedAssessment={props.lastSavedAssessment} onAiError={props.onAiError} onAssessmentUpdated={props.onAssessmentUpdated} onChange={props.onChange} onSaveFileChanges={props.onSaveFileChanges} selectedPendingFiles={props.selectedPendingFiles} />
            {showAcknowledgeModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Mark Control Assessment As Valid" areYouSureText="Are you sure you want to mark this control assessment as valid?" buttonText="Confirm" buttonLoadingText="Confirming..." buttonIcon={ICON_POSITIVE} performOperation={handleAcknowledgeUpdates} hideModal={() => setShowAcknowledgeModal(false)}>
                    <Text>Answers have been updated since the control was last assessed. Marking the assessment as valid indicates that the updated answers do not impact the validity of the control assessment.</Text>
                </ConfirmationModal>
            )}
        </div>
    );
};
